// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

// eslint-disable-next-line import/no-cycle
import {ModalAttrs, ModalEl} from 'common/components/modal';
import {BannerEl} from 'common/components/banner';
import {InputEl} from 'common/components/input';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {BannerType} from 'common/components/banner/types';
import {formatText} from 'common/util/format-text';

import {MarkdownEl} from '../markdown';
import type {Confirm} from './index';

export function template(this: Confirm) {
  const {t, print} = this.getContext(`i18n`);
  const {inputError, inputValue} = this.state;
  const description = this.params.description ? print(this.params.description) : this.attr(`description`);
  const proxyAttrs = {} as ModalAttrs;
  [`closeable`, `open`, `grow-to-content`, `not-fullscreen`, `modal-type`].forEach(
    (attr) => (proxyAttrs[attr] = this.attr(attr)),
  );

  const title = this.params.title ? print(this.params.title) : this.attr(`title`);

  return (
    <ModalEl attrs={proxyAttrs} on={{change: this.helpers.modalChanged}}>
      <div sel='.mp-confirm-container'>
        <div sel='.mp-confirm-body'>
          <div sel='.mp-confirm-body-content'>
            <>
              {this.attr(`banner-text`) ? (
                <BannerEl
                  sel='.mp-confirm-info-banner'
                  attrs={{
                    theme: `info`,
                    type: BannerType.Embedded,
                    'body-text': this.attr(`banner-text`),
                  }}
                />
              ) : null}
              {title ? <div sel='.mp-confirm-title'>{title}</div> : null}
              {description ? (
                this.attr(`description-allow-newline`) ? (
                  <div sel='.mp-confirm-description-newline'>
                    {formatText(description, {className: `confirm-description-newline`})}
                  </div>
                ) : this.attr(`description-use-markdown`) ? (
                  <div sel='.mp-confirm-description-newline'>
                    <MarkdownEl attrs={{text: description}} />
                  </div>
                ) : (
                  <div sel='.mp-confirm-description'>{description}</div>
                )
              ) : null}
              {this.attr(`show-input`) ? (
                <InputEl
                  sel='.mp-confirm-input'
                  attrs={{
                    elref: `confirmInput`,
                    'error-message': inputError,
                    'validation-status': inputError ? `invalid` : ``,
                  }}
                  hook={{
                    insert: this.helpers.onInputInserted,
                  }}
                  on={{
                    input: this.helpers.changeInputValue,
                    keyup: this.helpers.commitInputValue,
                    change: (ev) => ev.stopPropagation(),
                  }}
                  props={{
                    value: inputValue,
                  }}
                />
              ) : null}
              {this.bannerContent || <slot />}
            </>
          </div>
        </div>
        <div sel='.mp-confirm-buttons'>
          {!this.attr(`hide-cancel-button`) ? (
            this.attr(`theme`) !== `info` ? (
              <ButtonEl
                attrs={{theme: ButtonTheme.Tertiary, elref: `mpConfirmCancelButton`}}
                class={{
                  'mp-confirm-button-cancel': true,
                }}
                on={{click: this.helpers.cancel}}
              >
                {this.attr(`cancel-button-label`) || t(`common.cancel`)}
              </ButtonEl>
            ) : null
          ) : null}
          <ButtonEl
            attrs={{
              elref: `mpConfirmConfirmButton`,
              disabled: this.helpers.isConfirmButtonDisabled(),
              theme: ButtonTheme.Primary,
              type:
                this.attr(`theme`) === `delete` ? this.helpers.ButtonType.Destructive : this.helpers.ButtonType.Info,
            }}
            class={{
              'mp-confirm-button-accept': true,
            }}
            on={{click: this.helpers.confirm}}
          >
            {this.params.confirmButtonLabel
              ? print(this.params.confirmButtonLabel)
              : this.attr(`confirm-button-label`)
                ? this.attr(`confirm-button-label`)
                : this.attr(`theme`) === `delete`
                  ? t(`common.delete`)
                  : `Okay`}
          </ButtonEl>
        </div>
      </div>
    </ModalEl>
  );
}
